import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { supabase, getAuthToken } from "../lib/supabase";
import { Link } from "react-router-dom";
import { useUser } from "../lib/UserContext";
import { usePracticienKey } from "../lib/useCrypto";
import { decryptFernet } from "../lib/fernetUtils";
import { usePatientDirectory } from "../lib/PatientDirectoryContext";
import { useStaff } from "../lib/StaffContext";
import ReopeningDigestView from "../components/ReopeningDigestView";
import QuickMessageSuggestions from "../components/QuickMessageSuggestions";
import ReplyLanguageSelector from "../components/ReplyLanguageSelector";
import {
  applyQuickMessageSelection,
  filterQuickMessages,
  getQuickMessageTrigger,
} from "../lib/quickMessages.mjs";
import {
  getReplyLanguageLabel,
  normalizeReplyLanguageOverride,
  toReplyLanguageOverrideDbValue,
  type ReplyLanguageOverride,
} from "../lib/replyLanguage";
import {
  buildConversationMeta,
  buildReplyPreviewLabel,
} from "../lib/templateMessageIdentity.mjs";
import {
  mergeConversationMessagesForDisplay,
  resolveLatestValidProfileName,
} from "../lib/conversationMessages.mjs";
import {
  EMPTY_MESSAGE_LABEL,
  buildWhatsAppMediaPresentation,
  createTrustedMediaOrigins,
  parseLocationCoordinates,
  parseWhatsAppMessageContent,
  replyPreviewLabel,
} from "../lib/whatsappMessageContent.mjs";

type Msg = {
  id: string;
  utilisateur: string;
  role: "user" | "assistant" | "human" | "system";
  contenu_chiffre: string;
  created_at: string;
  profile_name?: string | null;
  sender_name?: string | null;
  is_template?: boolean | null;
  praticien_id: string;
  delivery_status?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  tag?: string | null;
  lu?: boolean | null;
  assigned_to?: string | null;
  assigned_at?: string | null;
  assignment_seen?: boolean | null;
  assignment_comment?: string | null;
  reply_to_sid?: string | null;
  message_sid?: string | null;
  detected_language?: string | null;
  translated_content?: string | null;
  patient_reaction?: string | null;
};

type QuickMessage = {
  id: string;
  display_name: string;
  shortcut: string;
  template_body: string;
};

function relativeDate(d: string) {
  const date = new Date(d);
  return date.toLocaleString();
}

const TRUSTED_MEDIA_ORIGINS = createTrustedMediaOrigins(
  typeof window !== "undefined" ? window.location.origin : "",
  import.meta.env.VITE_SUPABASE_URL
);

export default function ConversationViewDesktop({
  utilisateur,
  onOpenConversation,
}: {
  utilisateur: string;
  onOpenConversation?: (utilisateur: string) => void;
}) {
  const user = useUser();
  // All hooks must be called unconditionally (Rules of Hooks)
  const { key: practKey } = usePracticienKey();
  const { getDisplayNameForPhone } = usePatientDirectory();

  const [rows, setRows] = useState<Msg[]>([]);
  const [decrypted, setDecrypted] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [assistantOn, setAssistantOn] = useState<boolean | null>(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [quickMessages, setQuickMessages] = useState<QuickMessage[]>([]);
  const [quickMessageMatches, setQuickMessageMatches] = useState<QuickMessage[]>([]);
  const [activeQuickMessageIndex, setActiveQuickMessageIndex] = useState(0);
  const [quickMessageTrigger, setQuickMessageTrigger] =
    useState<ReturnType<typeof getQuickMessageTrigger>>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isRealtimeActive, setIsRealtimeActive] = useState(true);
  const activeDoctor = JSON.parse(localStorage.getItem("activeDoctor") || "{}");
  const { activeStaff } = useStaff();
  const assigneeId = activeStaff?.staff_id ?? activeDoctor?.id ?? null;
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("smileys");
  const [replyLanguageOverride, setReplyLanguageOverride] =
    useState<ReplyLanguageOverride>("auto");

  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingQuickMessageCaretRef = useRef<number | null>(null);

  const initialLoadRef = useRef(true);
  const stickToBottomRef = useRef(true);
  const [firstNewMsgId, setFirstNewMsgId] = useState<string | null>(null);
  const separatorRef = useRef<HTMLDivElement>(null);
  const firstNewMsgDetected = useRef(false);

  const isNearBottom = (threshold = 120) => {
    const el = scrollRef.current;
    if (!el) return true;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    return dist <= threshold;
  };

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  const handleUserScroll = () => {
    stickToBottomRef.current = isNearBottom();
  };

  // --- Meta 24h rule ---
  const lastUserMessage = rows
    .filter((m) => m.role === "user")
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  
  const lastMsgAgeMs = lastUserMessage
    ? Date.now() - new Date(lastUserMessage.created_at).getTime()
    : 0;
  
  const isMetaWindowExpired = lastUserMessage && lastMsgAgeMs > 24 * 3600 * 1000;
  const isMetaWindowWarning = lastUserMessage && lastMsgAgeMs > 23 * 3600 * 1000; // Avertissement dès 23h
  const threadPractitionerId =
    rows[0]?.praticien_id || activeDoctor?.id || user?.id || null;

  const resizeComposer = (target: HTMLTextAreaElement | null) => {
    if (!target) return;
    target.style.height = "auto";
    target.style.height = `${target.scrollHeight}px`;
  };

  const closeQuickMessageSuggestions = () => {
    setQuickMessageTrigger(null);
    setQuickMessageMatches([]);
    setActiveQuickMessageIndex(0);
  };

  const syncQuickMessageSuggestions = (value: string, caret: number) => {
    const nextTrigger = getQuickMessageTrigger(value, caret);
    setQuickMessageTrigger(nextTrigger);

    if (!nextTrigger) {
      setQuickMessageMatches([]);
      setActiveQuickMessageIndex(0);
      return;
    }

    const matches = filterQuickMessages(quickMessages, nextTrigger.query).slice(0, 5);
    setQuickMessageMatches(matches);
    setActiveQuickMessageIndex(0);
  };

  const insertQuickMessage = (quickMessage: QuickMessage) => {
    if (!quickMessageTrigger) return;

    const nextDraft = applyQuickMessageSelection(messageDraft, {
      ...quickMessage,
      start: quickMessageTrigger.start,
      end: quickMessageTrigger.end,
    });

    setMessageDraft(nextDraft.value);
    closeQuickMessageSuggestions();
    pendingQuickMessageCaretRef.current = nextDraft.caret;
  };

  const loadMessages = async (pageIndex = 1) => {
    const from = (pageIndex - 1) * 25;
    const to = from + 24;

    let q = supabase
      .from("conversations")
      .select(
        `id,utilisateur,role,contenu_chiffre,created_at,profile_name,sender_name,is_template,praticien_id,
        delivery_status,delivered_at,read_at,
        tag,lu,assigned_to,assigned_at,assignment_seen,assignment_comment,
        reply_to_sid,message_sid,detected_language,translated_content,patient_reaction`
      )
      .eq("utilisateur", utilisateur)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (user?.role !== "admin") q = q.eq("praticien_id", user?.id);

    const { data } = await q;

    if (data && data.length > 0) {
      const ordered = [...data].reverse();
      if (pageIndex === 1 && !firstNewMsgDetected.current) {
        firstNewMsgDetected.current = true;
        const hasRead = ordered.some((m) => m.lu === true);
        const firstNew = ordered.find((m) => m.lu === null || m.lu === false);
        if (firstNew && hasRead) setFirstNewMsgId(firstNew.id);
      }
      setRows((prev) => mergeConversationMessagesForDisplay(prev, ordered));
      if (data.length < 25) setHasMore(false);
    } else {
      setHasMore(false);
    }
  };

  useEffect(() => {
    if (!user || !utilisateur) return;
    setRows([]);
    setPage(1);
    setHasMore(true);
    setFirstNewMsgId(null);
    initialLoadRef.current = true;
    firstNewMsgDetected.current = false;
    loadMessages(1);
  }, [user, utilisateur]);

  useEffect(() => {
    if (!rows.length || !assigneeId) return;
    const hasTag = rows.some((r) => !!r.tag);
    if (hasTag) clearTag(utilisateur);
  }, [rows, utilisateur]);

  useEffect(() => {
    if (!user || !utilisateur) return;
  
    const markThreadAsRead = async () => {
      try {
        await supabase
          .from("conversations")
          .update({ lu: true })
          .eq("utilisateur", utilisateur)
          .eq("praticien_id", user.id)
          .or("lu.is.null,lu.eq.false");
  
        // 🔔 prévenir la ConversationList que ce thread est lu
        window.dispatchEvent(
          new CustomEvent("thread-read", { detail: utilisateur })
        );
      } catch (e) {
        console.error("[READ][ERROR] Impossible de marquer comme lu :", e);
      }
    };
  
    markThreadAsRead();
  }, [user, utilisateur]);

  useEffect(() => {
    if (!utilisateur || !threadPractitionerId) return;
    (async () => {
      const { data } = await supabase
        .from("bot_status")
        .select("status, reply_language_override")
        .eq("utilisateur", utilisateur)
        .eq("praticien_id", threadPractitionerId)
        .order("updated_at", { ascending: false })
        .limit(1);
      setAssistantOn(data?.[0]?.status !== "off");
      setReplyLanguageOverride(
        normalizeReplyLanguageOverride(data?.[0]?.reply_language_override)
      );
    })();
  }, [utilisateur, threadPractitionerId]);

  useEffect(() => {
    if (!threadPractitionerId) {
      setQuickMessages([]);
      closeQuickMessageSuggestions();
      return;
    }

    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("clinic_templates")
        .select("id, display_name, shortcut, template_body")
        .eq("praticien_id", threadPractitionerId)
        .eq("template_kind", "quick_message")
        .order("display_name");

      if (cancelled) return;

      if (error) {
        console.error("[quick-messages] load failed", error);
        setQuickMessages([]);
        closeQuickMessageSuggestions();
        return;
      }

      setQuickMessages((data ?? []) as QuickMessage[]);
    })();

    return () => {
      cancelled = true;
    };
  }, [threadPractitionerId]);

  useEffect(() => {
    if (pendingQuickMessageCaretRef.current == null) return;

    const caret = pendingQuickMessageCaretRef.current;
    pendingQuickMessageCaretRef.current = null;

    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
      resizeComposer(textarea);
    });
  }, [messageDraft]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    syncQuickMessageSuggestions(
      messageDraft,
      textarea.selectionStart ?? messageDraft.length
    );
  }, [quickMessages]);

  useEffect(() => {
    if (!practKey || user?.role === "admin") return;
    (async () => {
      const acc: Record<string, string> = {};
      for (const m of rows) {
        try {
          acc[m.id] = await decryptFernet(practKey, m.contenu_chiffre);
        } catch {
          acc[m.id] = "⛔️";
        }
      }
      setDecrypted(acc);
    })();
  }, [rows, practKey, user]);

  useLayoutEffect(() => {
    if (!initialLoadRef.current || rows.length === 0) return;
    initialLoadRef.current = false;
    if (separatorRef.current) {
      separatorRef.current.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "center" });
    } else {
      scrollToBottom("auto");
    }
  }, [rows.length]);

  // Realtime supabase
  useEffect(() => {
    if (!user || !utilisateur) return;

    const channel = supabase
      .channel(`conv_view_desktop:${utilisateur}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "conversations",
          filter: `utilisateur=eq.${utilisateur}`,
        },
        async (payload) => {
          const msg = payload.new as Msg;

          setRows((prev) => {
            if (
              prev.some(
                (m) =>
                  m.id === msg.id ||
                  (m.created_at === msg.created_at &&
                    msg.role === "human" &&
                    msg.contenu_chiffre === "")
              )
            ) {
              return prev;
            }
            return [...prev, msg];
          });

          const contenu = msg.contenu_chiffre || "";

          if (msg.role === "human") {
            setDecrypted((prev) => ({ ...prev, [msg.id]: contenu }));
          } else if (practKey && contenu && contenu.startsWith("gAAAA")) {
            try {
              const texte = await decryptFernet(practKey, contenu);
              setDecrypted((prev) => ({ ...prev, [msg.id]: texte }));
            } catch {
              setDecrypted((prev) => ({ ...prev, [msg.id]: "⛔️" }));
            }
          } else {
            setDecrypted((prev) => ({ ...prev, [msg.id]: contenu }));
          }

          scrollToBottom("smooth");
        }
      )

      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "conversations",
          filter: `utilisateur=eq.${utilisateur}`,
        },
        (payload) => {
          const updated = payload.new as Msg;
          setRows((prev) =>
            prev.map((m) =>
              m.id === updated.id ? { ...m, ...updated } : m
            )
          );
        }
      )
      
      .on("system", { event: "*" }, (status) => {
        console.log("Realtime status:", status);
        if (status === "closed" || status === "errored") {
          setIsRealtimeActive(false);
        } else if (status === "joined") {
          setIsRealtimeActive(true);
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, utilisateur, practKey]);

  // Polling fallback si realtime down
  useEffect(() => {
    if (!user || !utilisateur) return;
    if (isRealtimeActive) return;

    const refreshMessages = async () => {
      let q = supabase
        .from("conversations")
        .select(
          `id,utilisateur,role,contenu_chiffre,created_at,profile_name,praticien_id,
          delivery_status,delivered_at,read_at,
          tag,lu,assigned_to,assigned_at,assignment_seen,assignment_comment,
          reply_to_sid,message_sid,detected_language,translated_content,patient_reaction`
        )
        .eq("utilisateur", utilisateur)
        .order("created_at", { ascending: true })
        .limit(25);

      if (user?.role !== "admin") q = q.eq("praticien_id", user?.id);

      const { data } = await q;
      if (data) {
        setRows(data);
      }
    };

    const interval = setInterval(() => {
      refreshMessages();
    }, 3000);

    return () => clearInterval(interval);
  }, [user, utilisateur, isRealtimeActive]);

  const decryptedCount = Object.keys(decrypted).length;
  useEffect(() => {
    const ready =
      user?.role === "admin" || decryptedCount === rows.length;
    if (!ready || rows.length === 0) return;
    if (stickToBottomRef.current) {
      requestAnimationFrame(() => scrollToBottom("smooth"));
    }
  }, [rows.length, decryptedCount, user]);

  const toggleAssistant = async () => {
    if (!threadPractitionerId) return;
    
    const previousAssistantOn = assistantOn !== false;
    const newStatus = previousAssistantOn ? "off" : "on";
    setAssistantOn(!previousAssistantOn);
  
    // 1️⃣ Mise à jour du statut dans Supabase
    const { error } = await supabase
      .from("bot_status")
      .upsert(
        {
          utilisateur,
          praticien_id: threadPractitionerId,
          status: newStatus,
          reply_language_override: toReplyLanguageOverrideDbValue(
            replyLanguageOverride
          ),
          updated_at: new Date().toISOString(),
        },
        { onConflict: ["utilisateur", "praticien_id"] }
      );
  
    if (error) {
      setAssistantOn(previousAssistantOn);
      console.error("Erreur lors de l'upsert dans bot_status:", error);
      return;
    }
  
    // 2️⃣ Invalidation immédiate du cache Redis côté backend
    try {
      const token = await getAuthToken();
      await fetch(
        "https://whatsapp-healthcare.onrender.com/cache/clear-bot-status",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token ?? "" },
          body: JSON.stringify({
            utilisateur,
            praticien_id: threadPractitionerId,
          }),
        }
      );
    } catch (e) {
      console.error("Erreur purge cache:", e);
    }
  };

  const updateReplyLanguageOverride = async (
    nextOverride: ReplyLanguageOverride
  ) => {
    if (!threadPractitionerId) return;

    const previousOverride = replyLanguageOverride;
    setReplyLanguageOverride(nextOverride);

    const { error } = await supabase
      .from("bot_status")
      .upsert(
        {
          utilisateur,
          praticien_id: threadPractitionerId,
          status: assistantOn === false ? "off" : "on",
          reply_language_override: toReplyLanguageOverrideDbValue(nextOverride),
          updated_at: new Date().toISOString(),
        },
        { onConflict: ["utilisateur", "praticien_id"] }
      );

    if (error) {
      setReplyLanguageOverride(previousOverride);
      console.error("Erreur lors de la mise à jour de la langue de réponse:", error);
    }
  };

  async function clearTag(utilisateur: string) {
    try {
      const { error } = await supabase
        .from("conversations")
        .update({ tag: null })
        .eq("utilisateur", utilisateur)
        .eq("praticien_id", rows[0]?.praticien_id || user?.id)
        .select("id");
  
      if (error) console.error("[Tag] clear error:", error);
      else console.log("[Tag] clear OK");
    } catch (err) {
      console.error("[Tag] clear exception:", err);
    }
  }

  async function markAssignmentDone() {
    if (!assigneeId) {
      alert("No active user found");
      return;
    }
  
    try {
      // 🔥 1) Mets à jour TOUTES les lignes de ce thread pour ce praticien
      // Pas seulement celles qui avaient assigned_to !
      const { error, count } = await supabase
        .from("conversations")
        .update({
          assigned_to: null,
          assigned_at: null,
          assignment_seen: true,
          assignment_comment: null,
        })
        .eq("utilisateur", utilisateur)
        .eq("praticien_id", user.id)       // 👈 Critère principal : le praticien du compte
        .select("id", { count: "exact" });
  
      if (error) {
        console.error("[Assignment] Supabase error:", error);
        alert("❌ Erreur lors de la mise à jour de l'assignation.");
        return;
      }
  
      // 🔥 2) Invalidation du cache Redis (sinon ancienne assignation revient)
      const convToken = await getAuthToken();
      await fetch(
        `${import.meta.env.VITE_API_URL}/internal/cache/invalidate_conversations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: convToken ?? "" },
          body: JSON.stringify({
            praticien_id: user.id,
            utilisateur,
          }),
        }
      );
  
      // 🔥 3) Mise à jour immédiate locale dans ConversationView
      setRows((prev) =>
        prev.map((r) =>
          r.utilisateur === utilisateur
            ? {
                ...r,
                assigned_to: null,
                assigned_at: null,
                assignment_seen: true,
                assignment_comment: null,
              }
            : r
        )
      );
  
      // 🔥 4) Informer ConversationList instantanément (mise à jour UI immédiate)
      window.dispatchEvent(
        new CustomEvent("assignment-cleared", { detail: utilisateur })
      );
  
      console.log(`[Assignment] ${count} message(s) mis à jour`);
      alert("Task marked as done ✅");
    } catch (err) {
      console.error("[Assignment] Unexpected:", err);
      alert("❌ Unexpected error during assignment clear.");
    }
  }

  function getStatusIcon(status?: string) {
    switch (status) {
      case "queued":
      case "sent":
        return (
          <span
            title="Sent"
            className="text-gray-200 text-[11px] font-medium drop-shadow-sm"
          >
            ✓
          </span>
        );
      case "delivered":
        return (
          <span
            title="Delivered"
            className="text-gray-100 text-[11px] font-medium drop-shadow-sm"
          >
            ✓✓
          </span>
        );
      case "read":
        return (
          <span
            title="Read"
            className="text-white text-[11px] font-semibold drop-shadow-md"
          >
            ✓✓
          </span>
        );
      case "failed":
      case "undelivered":
        return (
          <span
            title="Error"
            className="text-yellow-200 text-[11px] font-semibold drop-shadow-sm"
          >
            ⚠️
          </span>
        );
      default:
        return null;
    }
  }

  const currentPractitionerId = rows[0]?.praticien_id || activeDoctor?.id || null;
  const directoryName = getDisplayNameForPhone(utilisateur, currentPractitionerId);
  const profileNameForPrefill = resolveLatestValidProfileName(rows);

  const displayName = (() => {
    if (directoryName) return directoryName;
  
    if (profileNameForPrefill && profileNameForPrefill.toLowerCase() !== "not available")
      return profileNameForPrefill;
  
    const previous = rows.find(
      (m) =>
        m.sender_name &&
        m.sender_name.toLowerCase() !== "not available"
    );
  
    if (previous?.sender_name) return previous.sender_name;
  
    return utilisateur.replace(/^whatsapp:/, "");
  })();
  
  const phoneForPrefill = utilisateur.replace(/^whatsapp:/, "");

  const currentAssignment = rows.find(
    (r) => r.assigned_to && r.assigned_to === assigneeId
  );
  const assignmentComment = currentAssignment?.assignment_comment || null;

  const EMOJI_CATEGORIES: Record<string, string[]> = {
    smileys: ["😀","😁","😂","🤣","😊","😍","🤗","😅","🥲","🙂","🙃","😉","😘","😇"],
    gestures: ["👍","🙏","👋","🤝","👌","✌️","🤙","👏","👊"],
    medical: ["💊","💉","🩺","🤒","🤕","🧪","🧬"],
    office: ["📅","⏰","📎","📄","📁","📝"],
  };
  
  const allEmojis = Object.values(EMOJI_CATEGORIES).flat();
  
  const filteredEmojis = emojiSearch.trim()
    ? allEmojis.filter((e) => e.includes(emojiSearch.trim()))
    : EMOJI_CATEGORIES[activeCategory] || [];

  // Branch: digest pseudo-route (after all hooks)
  if (utilisateur.startsWith("digest:")) {
    const digestId = utilisateur.slice("digest:".length);
    const praticienId = user?.role === "admin"
      ? (JSON.parse(localStorage.getItem("activeDoctor") || "{}").id ?? "")
      : (user?.id ?? "");
    return (
      <div className="flex flex-col h-full">
        <ReopeningDigestView
          digestId={digestId}
          praticienId={praticienId}
          onOpenConversation={onOpenConversation}
        />
      </div>
    );
  }

  return (
    <div className="conversation-wrapper flex flex-col h-full min-h-0 bg-white">
      {/* Header */}
      <div
        ref={headerRef}
        className="shrink-0 px-4 py-3 border-b flex items-center gap-3"
      >
        <div className="leading-tight">
          {isMetaWindowWarning && !isMetaWindowExpired && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md mt-1">
              ⚠️ The 24h WhatsApp window will expire soon. Please consider using a template.
            </div>
          )}
          
          {isMetaWindowExpired && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-md mt-1">
              ❗ The 24h WhatsApp window has expired. You cannot send a freeform message.
              Please use a WhatsApp template.
            </div>
          )}
           <div className="flex items-center gap-2 font-semibold">
            <span>{displayName}</span>
            {!directoryName && (
              <Link
                to="/patients"
                state={{
                  patientPrefill: {
                    full_name: profileNameForPrefill,
                    phone: phoneForPrefill,
                  },
                }}
                className="text-[11px] font-medium text-blue-700 border border-blue-100 rounded-md px-2 py-[2px] hover:bg-blue-50 transition"
              >
                Add info
              </Link>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {utilisateur.replace(/^whatsapp:/, "")}
          </div>
        </div>
        {/* ✅ Bloc central : action à réaliser */}
        {assignmentComment && (
          <div className="flex-1 text-center">
            <div className="text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1 inline-block shadow-sm">
              📝 {assignmentComment}
            </div>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          {loading && <div className="text-sm text-gray-500">Chargement…</div>}
          {assistantOn !== null && (
            <button
              onClick={toggleAssistant}
              className={`text-xs px-3 py-1 rounded-full font-medium border transition-colors ${
                assistantOn
                  ? "bg-green-100 text-green-700 border-green-300 hover:bg-green-200"
                  : "bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300"
              }`}
            >
              Assistant {assistantOn ? "ON" : "OFF"}
            </button>
          )}
        </div>
        {/* ✅ Bouton Mark as done (visible uniquement si assigné) */}
        {currentAssignment?.assigned_to && (
          <button
            onClick={markAssignmentDone}
            className="text-xs px-3 py-1 rounded-full font-medium border transition-colors bg-green-100 text-green-700 border-green-300 hover:bg-green-200 ml-2"
          >
            ✅ Mark as done
          </button>
        )}
      </div>

      {/* Scrollable */}
      <div
        ref={scrollRef}
        onScroll={handleUserScroll}
        className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-2 bg-[#fdfaf6]"
      >
        {hasMore && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => {
                const nextPage = page + 1;
                loadMessages(nextPage);
                setPage(nextPage);
              }}
              className="px-4 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-full shadow-sm hover:bg-blue-200 transition"
            >
              🔼 Load more chats…
            </button>
          </div>
        )}

        {rows.map((m) => {
          const texte =
            user?.role === "admin" ? "★ ★ ★" : decrypted[m.id] ?? "…";
          const date = relativeDate(m.created_at);
          const isFirstNew = firstNewMsgId === m.id;

          if (m.role === "system") {
            return (
              <div key={m.id} className="flex justify-center px-3 py-2">
                <div className="max-w-[90%] rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950 shadow-sm">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                    Attachment delivery notice
                  </div>
                  <div className="whitespace-pre-line text-sm leading-snug">
                    {texte.trim()}
                  </div>
                  <div className="mt-1 text-[10px] text-amber-700/70">{date}</div>
                </div>
              </div>
            );
          }

          const repliedMessage = (() => {
            if (!m.reply_to_sid) return null;
            return (
              rows.find(
                (r) =>
                  r.message_sid === m.reply_to_sid ||
                  r.id === m.reply_to_sid
              ) || null
            );
          })();

          const repliedPreview = (() => {
            if (!repliedMessage) return null;

            const repliedText =
              user?.role === "admin"
                ? "★ ★ ★"
                : decrypted[repliedMessage.id] ?? "…";

            const roleLabel =
              buildReplyPreviewLabel(repliedMessage, {
                displayName,
                defaultHumanLabel: "Team member",
              });

            const structuredPreview = replyPreviewLabel(repliedText);
            const preview = structuredPreview || repliedText.trim();

            return (
              <div className="mb-2 px-3 py-2 rounded-xl bg-black/10 border border-black/15 text-xs text-gray-900 shadow-sm">
                <div className="text-[10px] uppercase tracking-wide opacity-75 mb-1">
                  Replying to {roleLabel}
                </div>
                <div className="leading-snug break-words max-h-12 overflow-hidden">
                  {preview || "Original message unavailable"}
                </div>
              </div>
            );
          })();     

          let align = "justify-start";
          let bubbleStyle = "bg-gray-200 text-gray-900 rounded-bl-none";
          let meta = buildConversationMeta(m, date, {
            displayName,
            defaultHumanLabel: "Team member",
          });

          if (m.role === "assistant") {
            align = "justify-end";
            bubbleStyle = "bg-[#25D366] text-white rounded-br-none";
          } else if (m.role === "human") {
            align = "justify-end";
            bubbleStyle = "bg-blue-600 text-white rounded-br-none";
          }

          return (
            <div key={m.id}>
            {isFirstNew && (
              <div
                ref={separatorRef}
                className="flex items-center gap-3 my-3 px-2 select-none"
              >
                <div className="flex-1 h-px bg-[#25D366]/40" />
                <span className="text-[11px] font-medium text-[#25D366] bg-[#dcf8c6]/60 px-3 py-0.5 rounded-full border border-[#25D366]/30 whitespace-nowrap">
                  New messages
                </span>
                <div className="flex-1 h-px bg-[#25D366]/40" />
              </div>
            )}
            <div className={`flex ${align}${(m.role === "assistant" || m.role === "human") && m.patient_reaction ? " mb-3" : ""}`}>
              <div className="relative max-w-[80%]">
              <div
                className={`px-3 py-2 rounded-2xl shadow ${bubbleStyle}`}
              >
                <div className="text-[11px] opacity-70 mb-1 flex items-center gap-1">
                <span>
                    {meta}
                  </span>
                  {(m.role === "assistant" || m.role === "human") &&
                    getStatusIcon(m.delivery_status)}
                </div>
                {repliedPreview}
                <div className="whitespace-pre-line leading-snug mt-1">
                  {(() => {
                    const content = texte.trim();
                    const parsedContent = parseWhatsAppMessageContent(content);
                    const detailLines = parsedContent.lines.filter(Boolean);
                    const mediaPresentation = buildWhatsAppMediaPresentation(
                      parsedContent,
                      TRUSTED_MEDIA_ORIGINS
                    );

                  // Cas image (patient ou praticien)
                  if (parsedContent.kind === "image") {
                    return (
                      <div className="space-y-1">
                        {mediaPresentation.mode === "embedded" ? (
                          <a
                            href={mediaPresentation.embeddedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={mediaPresentation.embeddedUrl}
                              alt={mediaPresentation.ariaLabel}
                              className="max-w-[250px] rounded-lg cursor-pointer hover:opacity-90 shadow-sm"
                            />
                          </a>
                        ) : (
                          <span className="text-sm">Attachment unavailable</span>
                        )}
                        {mediaPresentation.filename && (
                          <div className="break-words text-sm">
                            {mediaPresentation.filename}
                          </div>
                        )}
                        {mediaPresentation.caption && (
                          <div className="whitespace-pre-line break-words text-sm">
                            {mediaPresentation.caption}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Cas fichier (PDF, DOCX, etc.)
                  if (parsedContent.kind === "file") {
                    return (
                      <div className="space-y-1">
                        {mediaPresentation.mode === "link" ? (
                          <a
                            href={mediaPresentation.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-blue-50 underline hover:text-blue-200"
                          >
                            📎 {mediaPresentation.filename || "File"}
                          </a>
                        ) : mediaPresentation.mode === "file-label" ? (
                          <span className="inline-flex items-center gap-2 text-sm text-blue-50">
                            📎 {mediaPresentation.filename || "File"}
                          </span>
                        ) : (
                          <span className="text-sm">Attachment unavailable</span>
                        )}
                        {mediaPresentation.caption && (
                          <div className="whitespace-pre-line break-words text-sm">
                            {mediaPresentation.caption}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (parsedContent.kind === "audio") {
                    return (
                      <div className="space-y-1">
                        {mediaPresentation.mode === "embedded" ? (
                          <audio
                            controls
                            preload="none"
                            src={mediaPresentation.embeddedUrl}
                            aria-label={mediaPresentation.ariaLabel}
                            className="max-w-full"
                          >
                            Audio playback unavailable.
                          </audio>
                        ) : mediaPresentation.mode === "link" ? (
                          <a
                            href={mediaPresentation.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm underline hover:opacity-80"
                          >
                            {mediaPresentation.linkLabel}
                          </a>
                        ) : (
                          <span className="text-sm">Attachment unavailable</span>
                        )}
                        {mediaPresentation.filename && (
                          <div className="break-words text-sm">
                            {mediaPresentation.filename}
                          </div>
                        )}
                        {mediaPresentation.caption && (
                          <div className="whitespace-pre-line break-words text-sm">
                            {mediaPresentation.caption}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (parsedContent.kind === "video") {
                    return (
                      <div className="space-y-1">
                        {mediaPresentation.mode === "embedded" ? (
                          <video
                            controls
                            preload="none"
                            src={mediaPresentation.embeddedUrl}
                            aria-label={mediaPresentation.ariaLabel}
                            className="max-w-[250px] rounded-lg shadow-sm"
                          >
                            Video playback unavailable.
                          </video>
                        ) : mediaPresentation.mode === "link" ? (
                          <a
                            href={mediaPresentation.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm underline hover:opacity-80"
                          >
                            {mediaPresentation.linkLabel}
                          </a>
                        ) : (
                          <span className="text-sm">Attachment unavailable</span>
                        )}
                        {mediaPresentation.filename && (
                          <div className="break-words text-sm">
                            {mediaPresentation.filename}
                          </div>
                        )}
                        {mediaPresentation.caption && (
                          <div className="whitespace-pre-line break-words text-sm">
                            {mediaPresentation.caption}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (parsedContent.kind === "sticker") {
                    return (
                      <div className="space-y-1">
                        {mediaPresentation.mode === "embedded" ? (
                          <img
                            src={mediaPresentation.embeddedUrl}
                            alt={mediaPresentation.ariaLabel}
                            className="max-h-40 max-w-[180px] object-contain"
                          />
                        ) : mediaPresentation.mode === "link" ? (
                          <a
                            href={mediaPresentation.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm underline hover:opacity-80"
                          >
                            {mediaPresentation.linkLabel}
                          </a>
                        ) : (
                          <span className="text-sm">Attachment unavailable</span>
                        )}
                        {mediaPresentation.filename && (
                          <div className="break-words text-sm">
                            {mediaPresentation.filename}
                          </div>
                        )}
                        {mediaPresentation.caption && (
                          <div className="whitespace-pre-line break-words text-sm">
                            {mediaPresentation.caption}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (parsedContent.kind === "location") {
                    const coordinates = parseLocationCoordinates(parsedContent.lines);
                    const locationText = detailLines.join("\n") || "Location unavailable";
                    return (
                      <div className="space-y-1">
                        <div className="whitespace-pre-line break-words text-sm">{locationText}</div>
                        {coordinates && (
                          <a
                            href={coordinates.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm underline hover:opacity-80"
                          >
                            Open in Google Maps
                          </a>
                        )}
                      </div>
                    );
                  }

                  if (parsedContent.kind === "contact") {
                    return (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase opacity-75">Contact</div>
                        <div className="whitespace-pre-line break-words text-sm">
                          {detailLines.join("\n") || "Contact details unavailable"}
                        </div>
                      </div>
                    );
                  }

                  if (parsedContent.kind === "template") {
                    return (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase opacity-75">Template</div>
                        <div className="whitespace-pre-line break-words text-sm">
                          {detailLines.join("\n") || "Template details unavailable"}
                        </div>
                      </div>
                    );
                  }

                  if (parsedContent.kind === "interactive") {
                    return (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold uppercase opacity-75">Interactive message</div>
                        <div className="whitespace-pre-line break-words text-sm">
                          {detailLines.join("\n") || "Interactive details unavailable"}
                        </div>
                      </div>
                    );
                  }

                  if (content === "[REACTION]") {
                    return <span>Reaction</span>;
                  }

                  // Texte traduit (patient)
                  if (
                    m.role === "user" &&
                    user?.role !== "admin" &&
                    m.translated_content &&
                    m.translated_content.trim()
                  ) {
                    const translated = m.translated_content.trim();
                    const sourceLang = (m.detected_language || "unknown").toUpperCase();
                    return (
                      <div className="space-y-1">
                        <span>{translated}</span>
                        <div className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-white/70 text-gray-700">
                          🌍 Translated from {sourceLang}
                        </div>
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[11px] opacity-80">Show original</summary>
                          <div className="mt-1 text-[12px] opacity-90 break-words">{content}</div>
                        </details>
                      </div>
                    );
                  }

                  // Assistant text translated for clinic staff
                  if (
                    m.role === "assistant" &&
                    user?.role !== "admin" &&
                    m.translated_content &&
                    m.translated_content.trim()
                  ) {
                    const translated = m.translated_content.trim();
                    const clinicLang = (m.detected_language || "unknown").toUpperCase();
                    return (
                      <div className="space-y-1">
                        <span>{translated}</span>
                        <div className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-white/70 text-gray-700">
                          Clinic translation ({clinicLang})
                        </div>
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[11px] opacity-80">Show message sent</summary>
                          <div className="mt-1 text-[12px] opacity-90 break-words">{content}</div>
                        </details>
                      </div>
                    );
                  }

                  // Translated text (practitioner → patient)
                  if (
                    m.role === "human" &&
                    m.translated_content &&
                    m.translated_content.trim()
                  ) {
                    const targetLang = (m.detected_language || "unknown").toUpperCase();
                    return (
                      <div className="space-y-1">
                        <span>{content}</span>
                        <div className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          🌐 Sent in {targetLang}
                        </div>
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[11px] opacity-80">Show translation sent</summary>
                          <div className="mt-1 text-[12px] opacity-90 break-words">{m.translated_content.trim()}</div>
                        </details>
                      </div>
                    );
                  }

                  // Texte normal
                  return <span>{parsedContent.raw || EMPTY_MESSAGE_LABEL}</span>;
                })()}
              </div>
              {(m.role === "assistant" || m.role === "human") && m.patient_reaction && (
                <div className="absolute -bottom-3 left-2 bg-white rounded-full shadow-sm px-2 py-0.5 text-sm border border-gray-100 leading-none select-none">
                  {m.patient_reaction}
                </div>
              )}
              </div>
              </div>
            </div>
            </div>
          );
        })}

        {rows.length === 0 && (
          <div className="text-center text-gray-500 mt-10">
            Aucun message.
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        ref={footerRef}
        className="shrink-0 border-t px-3 py-2 bg-white z-10 relative"
      >
        {/* 🛑 Assistant activé → écriture bloquée */}
        {assistantOn ? (
          <div className="w-full text-center text-sm text-gray-500 py-2">
            🛑 Please turn off the assistant before writing a message.
          </div>
        ) : isMetaWindowExpired ? (
          /* ❗ Fenêtre WhatsApp > 24h → écriture bloquée */
          <div className="w-full text-center text-sm text-red-600 py-3">
            ❗ The 24h WhatsApp window has expired.<br />
            You cannot send a freeform message.<br />
            Please use a WhatsApp template.
          </div>
        ) : (
          /* 📝 MODE NORMAL : on peut écrire */
          <form
            className="flex flex-col gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              if ((!messageDraft.trim() && !selectedFile) || !user) return;
      
              const formData = new FormData();
              formData.append("From", `whatsapp:${utilisateur}`);  
              formData.append("To", user?.twilio_numero || "+33600000000");
              formData.append(
                "Body",
                messageDraft.trim() || "[FILE]"
              );
              formData.append("ProfileName", user?.full_name || "not available");
              formData.append("HumanMessage", "true");
              // 🆕 Identification de la personne qui écrit
              formData.append(
                "SenderName",
                activeStaff?.name || activeDoctor?.full_name || user?.full_name || user?.email || "Practitioner"
              );
              formData.append(
                "SenderId",
                activeDoctor?.id || user?.id || ""
              );
              if (replyLanguageOverride !== "auto") {
                formData.append("ReplyLanguageOverride", replyLanguageOverride);
              }
      
              let uploadedMediaUrl: string | null = null;

              // 🖼️ Upload fichier
              if (selectedFile) {
                try {
                  const bucket = "clinic-files";
                  const filePath = `uploads/${user?.id || "unknown"}/${Date.now()}_${selectedFile.name}`;
                  const fileCopy = new File(
                    [await selectedFile.arrayBuffer()],
                    selectedFile.name,
                    { type: selectedFile.type }
                  );
                  
                  const { error: uploadError } = await supabase.storage
                    .from(bucket)
                    .upload(filePath, fileCopy);

      
                  if (uploadError) {
                    console.error("Erreur upload Supabase:", uploadError);
                  } else {
                    const { data: publicUrlData } = supabase.storage
                      .from(bucket)
                      .getPublicUrl(filePath);
                    if (publicUrlData?.publicUrl) {
                      uploadedMediaUrl = publicUrlData.publicUrl;
                      formData.append("MediaUrl0", publicUrlData.publicUrl);
                      formData.append("MediaContentType0", selectedFile.type);
                    }
                  }
                } catch (error) {
                  console.error("❌ Erreur upload:", error);
                }
              }
      
              // Ajout local immédiat
              const tempId = "temp-" + Date.now();
              setDecrypted((prev) => ({
                ...prev,
                [tempId]:
                  messageDraft ||
                  (selectedFile
                    ? `${selectedFile.type.startsWith("image/") ? `[IMAGE]` : `[FILE]`} ${
                        uploadedMediaUrl || selectedFile.name
                      }`
                    : ""),
              }));
              setMessageDraft("");
              closeQuickMessageSuggestions();
              setSelectedFile(null);
      
              const textarea = textareaRef.current;
              if (textarea) textarea.style.height = "40px";
      
              setTimeout(() => scrollToBottom("auto"), 300);
      
              // Envoi backend
              try {
                const res = await fetch(
                  "https://whatsapp-healthcare.onrender.com/praticien-message",
                  {
                    method: "POST",
                    body: formData,
                  }
                );
                if (!res.ok) {
                  const errText = await res.text();
                  console.error("❌ Erreur HTTP:", res.status, errText);
                } else {
                  console.log("✅ Message envoyé");
                }
              } catch (err) {
                console.error("❌ Erreur réseau:", err);
              }
            }}
          >
            {/* Aperçu du fichier sélectionné */}
            {selectedFile && (
              <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  {selectedFile.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="preview"
                      className="h-12 w-auto rounded"
                    />
                  ) : (
                    <span>📄 {selectedFile.name}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="text-gray-400 hover:text-red-500 transition"
                >
                  ✖
                </button>
              </div>
            )}
      
            <div className="flex items-end gap-2">
              {/* Upload fichier */}
              <label
                htmlFor="file-upload"
                className="cursor-pointer px-2 py-2 rounded-full hover:bg-gray-100 transition"
                title="Attach image or file"
              >
                📎
              </label>
              <input
                id="file-upload"
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
              />

              {/* Emoji Picker premium */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(v => !v)}
                  className="px-2 py-2 rounded-full hover:bg-gray-100 transition"
                  title="Insert emoji"
                >
                  😊
                </button>
              
                {showEmojiPicker && (
                  <div
                    className="absolute bottom-14 left-0 bg-white border rounded-2xl shadow-2xl w-64
                               overflow-hidden animate-fadeIn z-50"
                  >
                    {/* Barre de catégories */}
                    <div className="flex border-b bg-gray-50 px-2 gap-1">
                      {["smileys", "gestures", "medical", "office"].map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setActiveCategory(cat)}
                          className={`flex-1 text-center py-2 text-sm capitalize rounded-md transition
                            ${activeCategory === cat
                              ? "bg-white text-blue-600 font-semibold shadow border-b-2 border-blue-500"
                              : "text-gray-600 hover:bg-white/70"
                            }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
              
                    {/* Grille d’emojis */}
                    <div className="grid grid-cols-6 gap-2 p-2 max-h-48 overflow-y-auto">
                      {filteredEmojis.length === 0 && (
                        <div className="text-center text-gray-500 text-sm col-span-6 py-4">
                          No result
                        </div>
                      )}
                    
                      {filteredEmojis.map((e) => (
                        <button
                          key={e}
                          type="button"
                          className="text-2xl hover:bg-gray-100 rounded-lg p-1 transition"
                          onClick={() => {
                            // 1) insérer l’emoji dans le message
                            const nextValue = messageDraft + e;
                            setMessageDraft(nextValue);
                            syncQuickMessageSuggestions(nextValue, nextValue.length);
                            pendingQuickMessageCaretRef.current = nextValue.length;
                            setShowEmojiPicker(false);
                          }}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Texte */}
              <div className="flex-1 space-y-2">
                <textarea
                  ref={textareaRef}
                  value={messageDraft}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setMessageDraft(nextValue);
                    resizeComposer(e.target);
                    syncQuickMessageSuggestions(
                      nextValue,
                      e.target.selectionStart ?? nextValue.length
                    );
                  }}
                  onSelect={(e) => {
                    const target = e.currentTarget;
                    syncQuickMessageSuggestions(
                      target.value,
                      target.selectionStart ?? target.value.length
                    );
                  }}
                  onKeyDown={(e) => {
                    if (quickMessageMatches.length === 0) return;

                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setActiveQuickMessageIndex((current) =>
                        current >= quickMessageMatches.length - 1 ? 0 : current + 1
                      );
                      return;
                    }

                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setActiveQuickMessageIndex((current) =>
                        current <= 0 ? quickMessageMatches.length - 1 : current - 1
                      );
                      return;
                    }

                    if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") {
                      e.preventDefault();
                      insertQuickMessage(quickMessageMatches[activeQuickMessageIndex]);
                      return;
                    }

                    if (e.key === "Escape") {
                      e.preventDefault();
                      closeQuickMessageSuggestions();
                    }
                  }}
                  rows={1}
                  className="w-full border rounded-2xl px-4 py-2 bg-gray-50 resize-none overflow-hidden leading-snug"
                  style={{
                    fontSize: "16px",
                    minHeight: "40px",
                    maxHeight: "120px",
                  }}
                  placeholder="Write to your patient..."
                />

                <QuickMessageSuggestions
                  items={quickMessageMatches}
                  activeIndex={activeQuickMessageIndex}
                  onSelect={insertQuickMessage}
                />
              </div>

              <ReplyLanguageSelector
                value={replyLanguageOverride}
                onChange={updateReplyLanguageOverride}
              />
      
              {/* Send */}
              <button
                type="submit"
                className="px-3 py-2 text-sm rounded-full bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                Send
              </button>
            </div>
            {replyLanguageOverride !== "auto" && (
              <div className="pl-2 text-[11px] text-slate-500">
                Replies translated to {getReplyLanguageLabel(replyLanguageOverride)} in this conversation.
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
